import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, MessageCircle, Upload } from "lucide-react";
import { StatusPill } from "@/components/candidate/shared";
import { WHATSAPP_URL } from "@/components/landing/shared";

const API = process.env.REACT_APP_BACKEND_URL;

const inputClass =
    "w-full border border-ink/15 bg-ivory px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-forest";

const Field = ({ label, htmlFor, required, optional, children }) => (
    <div>
        <label htmlFor={htmlFor} className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55">
            {label}
            {required && <span className="ml-1 text-champagne" aria-hidden="true">*</span>}
            {optional && <span className="ml-2 normal-case tracking-normal text-ink/40">(optional)</span>}
        </label>
        {children}
    </div>
);

export const ApplicationForm = ({ job }) => {
    const [phase, setPhase] = useState("checking"); // checking | form | applied
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error
    const [errorMsg, setErrorMsg] = useState("");
    const [cvName, setCvName] = useState("");
    const [docNames, setDocNames] = useState([]);
    const [reference, setReference] = useState("");
    const [candidate, setCandidate] = useState(null);
    const [profile, setProfile] = useState(null);
    const [existingApp, setExistingApp] = useState(null);
    const [useSavedCv, setUseSavedCv] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const me = await fetch(`${API}/api/candidate/me`, { credentials: "include" });
                if (!me.ok) {
                    setPhase("form");
                    return;
                }
                const user = await me.json();
                setCandidate(user);
                const [profRes, appsRes] = await Promise.all([
                    fetch(`${API}/api/candidate/profile`, { credentials: "include" }),
                    fetch(`${API}/api/candidate/applications`, { credentials: "include" }),
                ]);
                if (profRes.ok) setProfile(await profRes.json());
                if (appsRes.ok) {
                    const apps = await appsRes.json();
                    const existing = apps.find((a) => a.job_id === job.id);
                    if (existing) {
                        setExistingApp(existing);
                        setPhase("applied");
                        return;
                    }
                }
                setPhase("form");
            } catch {
                setPhase("form");
            }
        })();
    }, [job.id]);

    const d = candidate
        ? {
              full_name: candidate.name || "",
              email: candidate.email || "",
              phone: profile?.phone || "",
              country_of_residence: profile?.country || "",
              nationality: profile?.nationality || "",
              current_location: profile?.city || "",
              profession: profile?.profession || "",
              years_experience: profile?.years_experience || "",
              highest_qualification: profile?.education_level || "",
              certifications: profile?.certifications || "",
              preferred_destination: (profile?.preferred_countries || "").split(",")[0]?.trim() || "",
          }
        : {};

    const hasSavedCv = Boolean(profile?.cv);
    const savedCvActive = hasSavedCv && useSavedCv;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status === "submitting") return;
        setStatus("submitting");
        setErrorMsg("");
        try {
            const form = e.target;
            const formData = new FormData(form);
            if (form.documents && form.documents.files.length === 0) {
                formData.delete("documents");
            }
            const res = await fetch(`${API}/api/applications`, {
                method: "POST",
                credentials: "include",
                body: formData,
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                const detail = body.detail;
                throw new Error(
                    typeof detail === "string"
                        ? detail
                        : "Your application could not be submitted. Please review the form and try again."
                );
            }
            const result = await res.json();
            setReference(result.reference || "");
            setStatus("success");
            document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
        } catch (err) {
            setErrorMsg(err.message);
            setStatus("error");
        }
    };

    if (phase === "checking") {
        return (
            <div className="flex min-h-40 items-center justify-center border border-ink/10 bg-sand p-10">
                <Loader2 size={22} className="animate-spin text-forest" />
            </div>
        );
    }

    if (phase === "applied" && existingApp) {
        return (
            <div data-testid="application-already-applied" className="border border-ink/10 bg-sand p-10 text-center">
                <CheckCircle2 size={40} strokeWidth={1.25} className="mx-auto text-forest" />
                <h3 className="mt-5 font-serif text-2xl font-medium tracking-tight text-ink">
                    Application Submitted
                </h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink/60">
                    You've already applied for this role. Your application{" "}
                    <span className="font-medium text-ink">{existingApp.reference}</span> is being handled
                    by our recruitment team.
                </p>
                <div className="mt-5 flex items-center justify-center gap-3">
                    <StatusPill status={existingApp.status} />
                </div>
                <a
                    href={`/account/applications/${existingApp.id}`}
                    data-testid="track-application-link"
                    className="mt-7 inline-flex items-center gap-2 bg-forest px-7 py-3.5 text-sm font-medium text-ivory transition-colors hover:bg-forest-deep"
                >
                    Track this application
                    <ArrowRight size={15} strokeWidth={1.5} />
                </a>
            </div>
        );
    }

    if (status === "success") {
        return (
            <div data-testid="application-success" className="flex min-h-72 flex-col items-center justify-center border border-ink/10 bg-sand p-10 text-center">
                <CheckCircle2 size={44} strokeWidth={1.25} className="text-forest" />
                <h3 className="mt-6 max-w-lg font-serif text-2xl font-medium tracking-tight text-ink">
                    Thank you. Your application has been received.
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-ink/60">
                    Our recruitment team will review your application for{" "}
                    <span className="font-medium text-ink">{job.title}</span> and contact you using the
                    details provided.
                </p>
                {reference && (
                    <p data-testid="application-reference" className="mt-6 border border-ink/15 bg-ivory px-6 py-4 text-sm text-ink/70">
                        Application reference:{" "}
                        <span className="font-serif text-lg font-semibold tracking-[0.1em] text-forest">
                            {reference}
                        </span>
                        <span className="mt-1 block text-xs text-ink/45">
                            Please quote this reference in any correspondence about your application.
                        </span>
                    </p>
                )}
                <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
                    {candidate && (
                        <a
                            href="/account/applications"
                            data-testid="application-dashboard-link"
                            className="inline-flex items-center gap-2 bg-forest px-7 py-3.5 text-sm font-medium text-ivory transition-colors hover:bg-forest-deep"
                        >
                            Track in my dashboard
                            <ArrowRight size={15} strokeWidth={1.5} />
                        </a>
                    )}
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="application-success-whatsapp"
                        className="inline-flex items-center gap-2 border border-forest/30 px-7 py-3.5 text-sm font-medium text-forest transition-colors hover:bg-forest hover:text-ivory"
                    >
                        <MessageCircle size={16} strokeWidth={1.5} />
                        Questions? WhatsApp Us
                    </a>
                </div>
            </div>
        );
    }

    return (
        <form data-testid="application-form" onSubmit={handleSubmit} className="border border-ink/10 bg-sand p-7 sm:p-10">
            <input type="hidden" name="job_id" value={job.id} />
            <input type="hidden" name="use_saved_cv" value={savedCvActive ? "true" : ""} />
            <input type="text" name="fax" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />

            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-forest">
                Applying for — {job.title}
            </p>
            {candidate && (
                <p data-testid="application-prefill-note" className="mt-3 text-xs leading-relaxed text-ink/50">
                    Signed in as {candidate.email} — your saved profile information has been added below.
                    Review and update anything before submitting.
                </p>
            )}

            <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <Field label="Full Name" htmlFor="ap-full-name" required>
                    <input id="ap-full-name" name="full_name" data-testid="ap-full-name" type="text" required defaultValue={d.full_name} placeholder="Your full name" className={inputClass} />
                </Field>
                <Field label="Email" htmlFor="ap-email" required>
                    <input id="ap-email" name="email" data-testid="ap-email" type="email" required defaultValue={d.email} placeholder="you@example.com" className={inputClass} />
                </Field>
                <Field label="Phone / WhatsApp" htmlFor="ap-phone" required>
                    <input id="ap-phone" name="phone" data-testid="ap-phone" type="tel" required defaultValue={d.phone} placeholder="+234 ..." className={inputClass} />
                </Field>
                <Field label="Country of Residence" htmlFor="ap-residence" required>
                    <input id="ap-residence" name="country_of_residence" data-testid="ap-residence" type="text" required defaultValue={d.country_of_residence} placeholder="Where you live now" className={inputClass} />
                </Field>
                <Field label="Nationality" htmlFor="ap-nationality" required>
                    <input id="ap-nationality" name="nationality" data-testid="ap-nationality" type="text" required defaultValue={d.nationality} placeholder="Your nationality" className={inputClass} />
                </Field>
                <Field label="Current Location" htmlFor="ap-location" optional>
                    <input id="ap-location" name="current_location" data-testid="ap-location" type="text" defaultValue={d.current_location} placeholder="City / State" className={inputClass} />
                </Field>
                <Field label="Profession" htmlFor="ap-profession" required>
                    <input id="ap-profession" name="profession" data-testid="ap-profession" type="text" required defaultValue={d.profession} placeholder="e.g. Registered Nurse" className={inputClass} />
                </Field>
                <Field label="Years of Experience" htmlFor="ap-experience" required>
                    <select id="ap-experience" name="years_experience" data-testid="ap-experience" required defaultValue={d.years_experience || ""} className={`${inputClass} cursor-pointer appearance-none`}>
                        <option value="" disabled>Select experience</option>
                        {["0–1 years", "1–3 years", "3–5 years", "5–10 years", "10+ years"].map((o) => (
                            <option key={o} value={o}>{o}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Highest Qualification" htmlFor="ap-qualification" required>
                    <input id="ap-qualification" name="highest_qualification" data-testid="ap-qualification" type="text" required defaultValue={d.highest_qualification} placeholder="e.g. BSc Nursing" className={inputClass} />
                </Field>
                <Field label="Relevant Certifications" htmlFor="ap-certifications" optional>
                    <input id="ap-certifications" name="certifications" data-testid="ap-certifications" type="text" defaultValue={d.certifications} placeholder="Licences, trade tests, certificates…" className={inputClass} />
                </Field>
                <Field label="Preferred Destination" htmlFor="ap-destination" required>
                    <input id="ap-destination" name="preferred_destination" data-testid="ap-destination" type="text" required defaultValue={d.preferred_destination} placeholder="e.g. Saudi Arabia, Canada" className={inputClass} />
                </Field>

                <div>
                    <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55">
                        CV<span className="ml-1 text-champagne" aria-hidden="true">*</span>
                    </span>
                    {hasSavedCv ? (
                        <div className="space-y-3">
                            <label className="flex cursor-pointer items-start gap-3 border border-ink/15 bg-ivory px-4 py-3.5 text-sm transition-colors hover:border-forest">
                                <input
                                    type="radio"
                                    name="cv_choice"
                                    data-testid="cv-use-saved"
                                    checked={useSavedCv}
                                    onChange={() => setUseSavedCv(true)}
                                    className="mt-0.5 accent-[#143D29]"
                                />
                                <span className="text-ink/75">
                                    Use my saved CV
                                    <span className="block truncate text-xs text-ink/45">{profile.cv.original}</span>
                                </span>
                            </label>
                            <label className="flex cursor-pointer items-start gap-3 border border-ink/15 bg-ivory px-4 py-3.5 text-sm transition-colors hover:border-forest">
                                <input
                                    type="radio"
                                    name="cv_choice"
                                    data-testid="cv-upload-new"
                                    checked={!useSavedCv}
                                    onChange={() => setUseSavedCv(false)}
                                    className="mt-0.5 accent-[#143D29]"
                                />
                                <span className="text-ink/75">Upload a different CV</span>
                            </label>
                        </div>
                    ) : null}
                    {(!hasSavedCv || !useSavedCv) && (
                        <>
                            <label htmlFor="ap-cv" className={`${hasSavedCv ? "mt-3 " : ""}flex cursor-pointer items-center justify-between gap-4 border border-dashed border-ink/25 bg-ivory px-4 py-4 text-sm transition-colors hover:border-forest`}>
                                <span className={`truncate ${cvName ? "font-medium text-ink" : "text-ink/45"}`}>
                                    {cvName || "Choose your CV file"}
                                </span>
                                <span className="inline-flex shrink-0 items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-forest">
                                    <Upload size={15} strokeWidth={1.5} />
                                    Browse
                                </span>
                            </label>
                            <input id="ap-cv" name="cv" data-testid="ap-cv-input" type="file" required={!savedCvActive} accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setCvName(e.target.files?.[0]?.name || "")} />
                        </>
                    )}
                    <p className="mt-2 text-xs text-ink/40">PDF, DOC or DOCX — up to 5 MB</p>
                </div>

                <div>
                    <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-ink/55">
                        Passport / Additional Documents<span className="ml-2 normal-case tracking-normal text-ink/40">(optional, up to 3)</span>
                    </span>
                    <label htmlFor="ap-docs" className="flex cursor-pointer items-center justify-between gap-4 border border-dashed border-ink/25 bg-ivory px-4 py-4 text-sm transition-colors hover:border-forest">
                        <span className={`truncate ${docNames.length ? "font-medium text-ink" : "text-ink/45"}`}>
                            {docNames.length ? docNames.join(", ") : "Passport, certificates, licences…"}
                        </span>
                        <span className="inline-flex shrink-0 items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-forest">
                            <Upload size={15} strokeWidth={1.5} />
                            Browse
                        </span>
                    </label>
                    <p className="mt-2 text-xs text-ink/40">PDF, DOC, DOCX, JPG or PNG — up to 5 MB each</p>
                    <input
                        id="ap-docs" name="documents" data-testid="ap-docs-input" type="file" multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden"
                        onChange={(e) => setDocNames(Array.from(e.target.files || []).slice(0, 3).map((f) => f.name))}
                    />
                </div>

                <div className="sm:col-span-2">
                    <Field label="Cover Letter / Message" htmlFor="ap-cover" optional>
                        <textarea id="ap-cover" name="cover_letter" data-testid="ap-cover" rows={4} placeholder="A short note about your experience and interest in this role…" className={`${inputClass} resize-none`} />
                    </Field>
                </div>

                <div className="sm:col-span-2">
                    <Field label="Additional Information" htmlFor="ap-additional" optional>
                        <textarea id="ap-additional" name="additional_information" data-testid="ap-additional" rows={3} placeholder="Availability, notice period, languages, or anything else we should know…" className={`${inputClass} resize-none`} />
                    </Field>
                </div>

                <div className="sm:col-span-2">
                    <label htmlFor="ap-consent" className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink/70">
                        <input id="ap-consent" name="consent" value="true" data-testid="ap-consent" type="checkbox" required className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#143D29]" />
                        <span>
                            I consent to Afrinexa Global processing my application information and documents
                            for recruitment purposes related to this role.
                            <span className="ml-1 text-champagne" aria-hidden="true">*</span>
                        </span>
                    </label>
                </div>
            </div>

            {status === "error" && (
                <div data-testid="application-error" className="mt-8 border border-ink/20 bg-ivory p-5 text-sm leading-relaxed text-ink/70">
                    {errorMsg} You can also reach us directly on{" "}
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-forest underline underline-offset-4">WhatsApp</a>.
                </div>
            )}

            <button
                type="submit"
                data-testid="application-submit-btn"
                disabled={status === "submitting"}
                className="mt-10 inline-flex w-full items-center justify-center gap-2 bg-forest px-10 py-5 text-sm font-semibold uppercase tracking-[0.12em] text-ivory transition-colors duration-300 hover:bg-forest-deep disabled:cursor-not-allowed disabled:opacity-80 sm:w-auto"
            >
                {status === "submitting" ? (
                    <>
                        <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
                        Submitting Application…
                    </>
                ) : (
                    <>
                        Submit Application
                        <ArrowRight size={16} strokeWidth={1.5} />
                    </>
                )}
            </button>
        </form>
    );
};
